/**
 * 再生中の <audio> に解析タップを張り、実スペクトルとカテゴリ推定を供給する。
 *
 * 実際に音を鳴らしているのはフローティングバーの 1 要素だけなので、この
 * フックもそこ 1 箇所で使う。得られたスペクトルは activeTrack 経由で一覧の
 * サムネイルへ配る。
 *
 * 安全側の設計:
 * - crossOrigin は CORS が実測済みで OK のときだけ付ける。付け外しは読み込みの
 *   やり直しになるので src 単位で固定し、再生中に切り替わらないようにする。
 * - ミュート中は要素の出力自体が無音になり解析値が取れないため、スペクトルを
 *   供給しない（サムネイル側は peaks 由来の動きに戻る）。
 */

import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  attachSpectrum,
  isMediaCorsUsable,
  markMediaCorsUnusable,
  resumeAudioContext,
  SpectrumSource,
} from './audioAnalyser';
import { SpectrumFrame } from './spectrumFeatures';
import {
  createSpectrumEstimator,
  FRAME_RATE,
  getSpectrumCategory,
  setSpectrumCategory,
} from './spectrumCategory';

export interface SpectrumAnalysis {
  /** <audio crossOrigin> に渡す値。CORS 未確認なら undefined（付けない） */
  crossOrigin: 'anonymous' | undefined;
  /** 現フレームの特徴量。解析できていなければ null */
  readSpectrum: () => SpectrumFrame | null;
  /**
   * <audio> の error に繋ぐ。crossOrigin が原因で読めなかった可能性があるなら
   * 解析を諦めて true を返すので、呼び出し側は素の要素で読み直すこと。
   * false のときは解析と無関係な失敗なので、通常のエラー処理に回す。
   */
  handleMediaError: () => boolean;
}

export function useSpectrumAnalysis(
  audioRef: RefObject<HTMLAudioElement | null>,
  src: string | null | undefined,
  isPlaying: boolean,
  isMuted: boolean,
): SpectrumAnalysis {
  // 解析のために crossOrigin を付けたら読み込みに失敗した src。音を鳴らすことが
  // 最優先なので、そうなったら解析を捨てる
  const [blockedSrc, setBlockedSrc] = useState<string | null>(null);

  // src の読み込み前に決まっている必要があるので、src ごとに一度だけ評価する
  const crossOrigin = useMemo(
    () => (src !== blockedSrc && isMediaCorsUsable(src) ? ('anonymous' as const) : undefined),
    [src, blockedSrc],
  );

  const sourceRef = useRef<SpectrumSource | null>(null);
  const canAnalyse = Boolean(crossOrigin) && isPlaying && !isMuted;

  const handleMediaError = useCallback((): boolean => {
    if (!src || !crossOrigin) return false;
    console.warn('[audioAnalyser] crossOrigin 付きで読み込めなかったため解析を無効化します');
    markMediaCorsUnusable(src);
    setBlockedSrc(src);
    return true;
  }, [src, crossOrigin]);

  useEffect(() => {
    return () => {
      sourceRef.current?.release();
      sourceRef.current = null;
    };
  }, [src]);

  // src も依存に含める: 連続再生のままトラックが切り替わると（pause イベントが
  // 出ないため）canAnalyse が変化せず、上の cleanup で外したタップが二度と
  // 張り直されない。src の変化で明示的に再アタッチする。
  useEffect(() => {
    if (!canAnalyse) return;
    resumeAudioContext();
    if (sourceRef.current) return;
    const element = audioRef.current;
    if (element) sourceRef.current = attachSpectrum(element);
  }, [canAnalyse, src, audioRef]);

  // 再生中にカテゴリを推定し直す。確定したら記録して打ち切る。
  // オフライン解析（offlineSpectrum）が効かなかった音源のための保険で、
  // 判定済みなら何もしない
  useEffect(() => {
    if (!canAnalyse || !src) return;
    if (getSpectrumCategory(src)) return;

    // 尺は効果音かどうかの判断に効く。読み込み前なら NaN なので渡さない
    const duration = audioRef.current?.duration;
    const estimator = createSpectrumEstimator(
      typeof duration === 'number' && Number.isFinite(duration) ? duration : null,
    );

    // 分類器の時間方向の指標（無音率・オンセット間隔）は FRAME_RATE を
    // 前提に較正してある。rAF は環境によって 60Hz にも 120Hz にもなるので、
    // そのまま流し込むと尺度がずれる。間引いて供給レートを固定する
    const intervalMs = 1000 / FRAME_RATE;
    let lastPushMs = -Infinity;

    let frameId = requestAnimationFrame(function tick(nowMs) {
      if (nowMs - lastPushMs >= intervalMs) {
        const frame = sourceRef.current?.read();
        if (frame) {
          lastPushMs = nowMs;
          estimator.push(frame);
          const result = estimator.estimate();
          if (result) {
            setSpectrumCategory(src, result.category);
            return;
          }
        }
      }
      frameId = requestAnimationFrame(tick);
    });

    return () => cancelAnimationFrame(frameId);
  }, [canAnalyse, src, audioRef]);

  const readSpectrum = useCallback(
    () => (canAnalyse ? (sourceRef.current?.read() ?? null) : null),
    [canAnalyse],
  );

  return { crossOrigin, readSpectrum, handleMediaError };
}
